import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { FeatureGateRoute } from "./components/FeatureGateRoute";
import {
  FinanceRoute,
} from "./components/FinanceRoute";
import { PlatformAdminRoute } from "./components/PlatformAdminRoute";
import { OrgAdminRoute } from "./components/OrgAdminRoute";
import { BooksRoute } from "./features/ledger/components/BooksRoute";
import { FeatureAddonRoute } from "./components/FeatureAddonRoute";
import { AgencyRoute } from "./components/AgencyRoute";
import { AppShell } from "./components/AppShell";
import { SettingsLayout } from "./components/SettingsLayout";
import { SocialHubLayout } from "./features/social/SocialHubLayout";
import { FEATURE_GATES } from "./features/plan/types";

// Auth pages — eagerly loaded (critical path)
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import AuthCallbackMeta from "./pages/AuthCallbackMeta";
import AuthCallbackLinkedIn from "./pages/AuthCallbackLinkedIn";
import AuthCallbackM365 from "./pages/AuthCallbackM365";
import AuthCallbackGoogle from "./pages/AuthCallbackGoogle";
import AcceptInvite from "./pages/AcceptInvite";
import ResetPassword from "./pages/ResetPassword";

// Lazy-loaded pages — code-split into separate chunks
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FirstRunSetupPage = lazy(() => import("./features/onboarding/FirstRunSetupPage"));
const ContactsList = lazy(() => import("./features/contacts/ContactsList"));
const ContactsImport = lazy(() => import("./features/contacts/ContactsImport"));
const DuplicateContactsPage = lazy(() => import("./features/contacts/DuplicateContactsPage"));
const OrdersImport = lazy(() => import("./features/orders/OrdersImport"));
const ContactDetail = lazy(() => import("./features/contacts/ContactDetail"));
const FollowupsPage = lazy(() => import("./features/followups/FollowupsPage"));
const CompaniesList = lazy(() => import("./features/companies/CompaniesList"));
const CompaniesImport = lazy(() => import("./features/companies/CompaniesImport"));
const CompanyDetail = lazy(() => import("./features/companies/CompanyDetail"));
const DealsKanban = lazy(() => import("./features/deals/DealsKanban"));
const DealDetail = lazy(() => import("./features/deals/DealDetail"));
const SocialOverview = lazy(() => import("./features/social/overview/SocialOverview"));
const SocialAccountsSettings = lazy(() => import("./features/social/SocialAccountsSettings"));
const ComposePage = lazy(() => import("./features/social/compose/ComposePage"));
const SocialCalendarPage = lazy(() => import("./features/social/calendar/SocialCalendarPage"));
const EngagementFeedPage = lazy(() =>
  import("@/features/social/engagement/EngagementFeedPage").then(
    (m) => ({ default: m.EngagementFeedPage }),
  ),
);
const Tasks = lazy(() => import("./pages/Tasks"));
const NotesPage = lazy(() => import("./features/notes/NotesPage"));
const Jobs = lazy(() => import("./pages/Jobs"));
const OrgBookingPages = lazy(() =>
  import("@/features/bookings/admin/OrgBookingPages").then((m) => ({ default: m.OrgBookingPages })),
);
const SmsInboxPage = lazy(() => import("./pages/SmsInboxPage"));
const InboxPage = lazy(() =>
  import("./features/inbox/InboxPage").then((m) => ({ default: m.InboxPage })),
);
const SequencesList = lazy(() => import("./features/sequences/SequencesList"));
const SequenceEditor = lazy(() => import("./features/sequences/SequenceEditor"));
const FormsList = lazy(() => import("./features/forms/FormsList"));
const FormEditorPage = lazy(() => import("./features/forms/FormEditorPage"));
const FormSubmissionsPage = lazy(() => import("./features/forms/FormSubmissionsPage"));
const BookingMenu = lazy(() => import("./pages/BookingMenu"));
const BookingSlotPicker = lazy(() => import("./pages/BookingSlotPicker"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const BookingInvoiced = lazy(() => import("./pages/BookingInvoiced"));
const BookingCancel = lazy(() => import("./pages/BookingCancel"));
const BookingReschedule = lazy(() => import("./pages/BookingReschedule"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Settings = lazy(() => import("./pages/Settings"));
const PlatformOrganizations = lazy(
  () => import("./pages/admin/platform/Organizations"),
);
const PlatformUsers = lazy(() => import("./pages/admin/platform/Users"));
const PlatformOrganizationDetail = lazy(
  () => import("./pages/admin/platform/OrganizationDetail"),
);
const PlatformAuditLogs = lazy(() => import("./pages/admin/platform/AuditLogs"));
const PlatformObservability = lazy(
  () => import("./pages/admin/platform/Observability"),
);
const PlatformUserDetail = lazy(
  () => import("./pages/admin/platform/UserDetail"),
);
const PlatformBilling = lazy(() => import("./pages/admin/platform/Billing"));
const PlatformSupportSessions = lazy(
  () => import("./pages/admin/platform/SupportSessions"),
);
const PlatformImpersonation = lazy(
  () => import("./pages/admin/platform/Impersonation"),
);
const PlatformFeatureFlags = lazy(
  () => import("./pages/admin/platform/FeatureFlags"),
);
const PlatformDashboard = lazy(
  () => import("./pages/admin/platform/Dashboard"),
);

// Agency self-serve — lazy pages gated by AgencyRoute
const AgencyDashboard = lazy(() => import("./features/agency/AgencyDashboard"));
const AgencyClientList = lazy(() => import("./features/agency/AgencyClientList"));
const AgencyClientDetail = lazy(() => import("./features/agency/AgencyClientDetail"));
const AgencyPlanEditor = lazy(() => import("./features/agency/AgencyPlanEditor"));
const BookingSettingsPage = lazy(() =>
  import("@/features/bookings/admin/BookingSettingsPage").then((m) => ({ default: m.BookingSettingsPage })),
);
const DocumentPublicView = lazy(() => import("@/pages/DocumentPublicView"));
const PublicFormPage = lazy(() => import("@/pages/PublicFormPage"));
const DocumentsListPage = lazy(() =>
  import("@/features/documents/admin/DocumentsListPage").then((m) => ({ default: m.DocumentsListPage })),
);
const DocumentEditorPage = lazy(() =>
  import("@/features/documents/admin/DocumentEditorPage").then((m) => ({ default: m.DocumentEditorPage })),
);
const ProjectsListPage = lazy(() =>
  import("@/features/projects/admin/ProjectsListPage").then((m) => ({ default: m.ProjectsListPage })),
);
const FinanceDashboardPage = lazy(() =>
  import("@/features/finance/FinanceDashboardPage").then((m) => ({ default: m.FinanceDashboardPage })),
);
const BooksOverviewPage = lazy(() =>
  import("@/features/ledger/BooksOverviewPage").then((m) => ({ default: m.BooksOverviewPage })),
);
const BooksSetupPage = lazy(() =>
  import("@/features/ledger/setup/BooksSetupPage").then((m) => ({ default: m.BooksSetupPage })),
);
const LedgerAccountsPage = lazy(() =>
  import("@/features/ledger/AccountsPage").then((m) => ({ default: m.AccountsPage })),
);
const JournalPage = lazy(() => import("@/features/ledger/JournalPage").then((m) => ({ default: m.JournalPage })));
const EntryEditorPage = lazy(() =>
  import("@/features/ledger/EntryEditorPage").then((m) => ({ default: m.EntryEditorPage })),
);
const EntryDetailPage = lazy(() =>
  import("@/features/ledger/EntryDetailPage").then((m) => ({ default: m.EntryDetailPage })),
);
const GeneralLedgerPage = lazy(() =>
  import("@/features/ledger/GeneralLedgerPage").then((m) => ({ default: m.GeneralLedgerPage })),
);
const TrialBalancePage = lazy(() =>
  import("@/features/ledger/TrialBalancePage").then((m) => ({ default: m.TrialBalancePage })),
);
const ClosePage = lazy(() => import("@/features/ledger/ClosePage").then((m) => ({ default: m.ClosePage })));
const ProjectDetailPage = lazy(() =>
  import("@/features/projects/admin/ProjectDetailPage").then((m) => ({ default: m.ProjectDetailPage })),
);
const Automations = lazy(() => import("@/pages/settings/Automations"));
const AutomationsDeadLetter = lazy(() => import("@/pages/settings/AutomationsDeadLetter"));
const AutomationConfigure = lazy(() => import("@/pages/settings/AutomationConfigure"));
const CustomAutomations = lazy(() => import("@/pages/settings/CustomAutomations"));
const ReorderRules = lazy(() => import("@/pages/settings/ReorderRules"));
const InactivityRules = lazy(() => import("@/pages/settings/InactivityRules"));
const ScoreThresholdRules = lazy(() => import("@/pages/settings/ScoreThresholdRules"));
const DateRules = lazy(() => import("@/pages/settings/DateRules"));
const StageRules = lazy(() => import("@/pages/settings/StageRules"));
const CiRuns = lazy(() => import("@/pages/settings/CiRuns"));
const CrmFields = lazy(() => import("@/pages/settings/CrmFields"));
const SettingsModules = lazy(() => import("@/pages/settings/Modules"));
const ReportingPage = lazy(() =>
  import("@/features/reporting/ReportingPage").then((m) => ({ default: m.ReportingPage })),
);
const UgcBoardPage = lazy(() => import("@/features/ugc/UgcBoardPage"));
const EmailCampaignsPage = lazy(() => import("@/features/emails/EmailCampaignsPage"));
const TemplatesPage = lazy(() => import("@/features/email-templates/TemplatesPage"));
const EmailCampaignEditorPage = lazy(() => import("@/features/emails/EmailCampaignEditorPage"));
const EmailCampaignGuidedWizard = lazy(() => import("@/features/emails/EmailCampaignGuidedWizard"));
const EmailCampaignAnalyticsPage = lazy(() => import("@/features/emails/EmailCampaignAnalyticsPage"));
const ArticlesPage = lazy(() => import("@/features/articles/ArticlesPage"));
const SmsCampaignsPage = lazy(() => import("@/features/sms-campaigns/SmsCampaignsPage"));
const SmsCampaignEditorPage = lazy(() => import("@/features/sms-campaigns/SmsCampaignEditorPage"));
const SmsCampaignGuidedWizard = lazy(() => import("@/features/sms-campaigns/SmsCampaignGuidedWizard"));
const SmsCampaignAnalyticsPage = lazy(() => import("@/features/sms-campaigns/SmsCampaignAnalyticsPage"));
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const WebhooksSettingsPage = lazy(() =>
  import("./features/settings/webhooks/WebhooksSettingsPage").then((m) => ({
    default: m.WebhooksSettingsPage,
  })),
);
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));

const PortalSettingsPage = lazy(() =>
  import("@/features/settings/portal/PortalSettingsPage").then((m) => ({
    default: m.PortalSettingsPage,
  })),
);
const BrandingSettingsPage = lazy(() =>
  import("@/features/settings/branding/BrandingSettingsPage").then((m) => ({
    default: m.BrandingSettingsPage,
  })),
);
const PublicReviewPage = lazy(() => import("@/pages/PublicReviewPage"));
const ReviewSettingsPage = lazy(() =>
  import("@/features/reviews/ReviewSettingsPage").then((m) => ({
    default: m.ReviewSettingsPage,
  })),
);
const ReviewRequestsList = lazy(() =>
  import("@/features/reviews/ReviewRequestsList").then((m) => ({
    default: m.ReviewRequestsList,
  })),
);
const ReferralsList = lazy(() => import("@/features/referrals/ReferralsList"));
const ReferralPartnersPage = lazy(() =>
  import("@/features/referrals/ReferralPartnersPage").then((m) => ({
    default: m.ReferralPartnersPage,
  })),
);
const LeadRoutingPage = lazy(() =>
  import("@/features/lead-routing/LeadRoutingPage").then((m) => ({
    default: m.LeadRoutingPage,
  })),
);
const PipelinesSettingsPage = lazy(() =>
  import("@/features/settings/pipelines/PipelinesSettingsPage").then((m) => ({
    default: m.PipelinesSettingsPage,
  })),
);
const CallsPage = lazy(() => import("@/features/calls/CallsPage"));
const ContactSegmentsPage = lazy(() => import("@/features/contacts/segments/ContactSegmentsPage"));

/** Minimal loading indicator shown while lazy chunks load */
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

/**
 * Reads the post id from the route and renders the composer in edit mode.
 * ComposePage doesn't render its own chrome, so wrap it in AppShell here to
 * match the rest of the authenticated app.
 */
function ComposeRoute() {
  const { id } = useParams<{ id: string }>();
  return (
    <AppShell>
      <ComposePage postId={id} />
    </AppShell>
  );
}

/**
 * Back-compat redirect: the CI run detail page moved from
 * /settings/ci/:runId to /admin/platform/ci/:runId. Preserve the run id.
 */
function CiRunRedirect() {
  const { runId } = useParams<{ runId: string }>();
  return <Navigate to={`/admin/platform/ci/${runId}`} replace />;
}

/**
 * Back-compat redirect for former /settings/* routes that carried a path
 * param (e.g. /settings/automations/:workflowId, /settings/webhooks/:id)
 * and have moved under /admin. Appends whichever param is present.
 */
function RedirectWithParams({ to }: { to: string }) {
  const params = useParams();
  const suffix = params.workflowId ?? params.id ?? params.pageId;
  return <Navigate to={suffix ? `${to}/${suffix}` : to} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      {/* Team invite acceptance — public; the page self-signs-up the invitee
          and completes membership via the relay-accept-invite EF. */}
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      {/* Meta OAuth callback. Not wrapped in ProtectedRoute because the user
          is mid-OAuth-redirect; the page itself handles the post-callback
          token exchange + bounces to /social/accounts on success. */}
      <Route path="/auth/callback/meta" element={<AuthCallbackMeta />} />
      <Route path="/auth/callback/linkedin" element={<AuthCallbackLinkedIn />} />
      {/* M365 (full Relay scope set) OAuth callback. Same pattern as Meta. */}
      <Route path="/auth/callback/m365_full" element={<AuthCallbackM365 />} />
      <Route path="/auth/callback/google_calendar" element={<AuthCallbackGoogle />} />

      {/* Public booking pages — no auth required */}
      <Route path="/book/:orgSlug/:userSlug" element={<BookingMenu />} />
      {/* Public document view — no auth required */}
      <Route path="/d/:viewToken" element={<DocumentPublicView />} />
      {/* Public lead-capture form — no auth required */}
      <Route path="/f/:orgSlug/:formSlug" element={<PublicFormPage />} />
      {/* Public client portal — no auth required. /portal/:token stashes token
          then redirects to /portal to keep the URL clean. */}
      <Route
        path="/portal/:token"
        element={<Suspense fallback={null}><ClientPortalPage /></Suspense>}
      />
      <Route
        path="/portal"
        element={<Suspense fallback={null}><ClientPortalPage /></Suspense>}
      />
      <Route path="/book/:orgSlug/:userSlug/:eventTypeSlug" element={<BookingSlotPicker />} />
      <Route path="/booking/confirmed/:confirmationToken" element={<BookingConfirmation />} />
      <Route path="/booking/invoiced/:bookingId" element={<BookingInvoiced />} />
      <Route path="/booking/cancel/:token" element={<BookingCancel />} />
      <Route path="/booking/reschedule/:token" element={<BookingReschedule />} />
      {/* Public privacy policy — gettheinspirededge.com/privacy. Required for
          Meta App Review + Stripe activation; no auth. */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/data-deletion" element={<DataDeletion />} />

      {/* Legacy Relay-in-monolith paths: the deployed standalone app uses
          unprefixed routes, but keep these redirects for recovered /relay/*
          navigation links and bookmarks. */}
      <Route path="/relay" element={<Navigate to="/dashboard" replace />} />
      <Route path="/relay/contacts" element={<Navigate to="/contacts" replace />} />
      <Route path="/relay/deals" element={<Navigate to="/deals" replace />} />
      <Route path="/relay/settings" element={<Navigate to="/settings" replace />} />

      {/* Authenticated */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      {/* First-run setup journey (#52). The page also redirects to
          /dashboard itself while the first_run_setup flag is off or during
          an agency session. */}
      <Route
        path="/setup"
        element={<ProtectedRoute><OrgAdminRoute><FirstRunSetupPage /></OrgAdminRoute></ProtectedRoute>}
      />

      {/* Contacts. /import must be defined BEFORE /:id so it doesn't match
          as a contact id in the parameterized route. */}
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <ContactsList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/segments"
        element={
          <ProtectedRoute>
            <ContactSegmentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/import"
        element={
          <ProtectedRoute>
            <ContactsImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/duplicates"
        element={
          <ProtectedRoute>
            <DuplicateContactsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts/:id"
        element={
          <ProtectedRoute>
            <ContactDetail />
          </ProtectedRoute>
        }
      />

      {/* Orders. Currently a single page: MONAT CSV importer. Sibling to
          /contacts/import — order rows attach to existing contacts (or create
          new ones) via the review-queue match flow. */}
      <Route
        path="/orders/import"
        element={
          <ProtectedRoute>
            <OrdersImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/follow-ups"
        element={
          <ProtectedRoute>
            <FollowupsPage />
          </ProtectedRoute>
        }
      />

      {/* Companies (Accounts). Keep /import before /:id so import traffic
          does not get treated as a company id. */}
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <CompaniesList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/import"
        element={
          <ProtectedRoute>
            <CompaniesImport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies/:id"
        element={
          <ProtectedRoute>
            <CompanyDetail />
          </ProtectedRoute>
        }
      />

      {/* Deals */}
      <Route
        path="/deals"
        element={
          <ProtectedRoute>
            <DealsKanban />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deals/:id"
        element={
          <ProtectedRoute>
            <DealDetail />
          </ProtectedRoute>
        }
      />

      {/* SMS Inbox — conversation list grouped by contact */}
      <Route
        path="/sms"
        element={
          <ProtectedRoute>
            <SmsInboxPage />
          </ProtectedRoute>
        }
      />

      {/* Unified Inbox — all-channel conversation view */}
      <Route
        path="/inbox"
        element={
          <ProtectedRoute>
            <AppShell>
              <InboxPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* SMS Sequences — list + editor */}
      <Route
        path="/sequences"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sequences}>
              <SequencesList />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sequences}>
              <SequenceEditor />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Lead-capture Forms — list, editor, submissions.
          /forms/:id/submissions must come before /forms/:id to avoid ambiguity. */}
      <Route
        path="/forms"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.forms}>
              <FormsList />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:id/submissions"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.forms}>
              <FormSubmissionsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.forms}>
              <FormEditorPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Social hub — Overview is the landing tab; Calendar, Engagements, and
          Accounts are sibling tabs under a shared layout (SocialHubLayout owns
          the AppShell + tab nav). The composer is a full-screen surface and
          stays outside the hub chrome (below). */}
      <Route
        path="/social"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.social}>
              <SocialHubLayout />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<SocialOverview />} />
        <Route path="calendar" element={<SocialCalendarPage />} />
        <Route path="engagements" element={<EngagementFeedPage />} />
        <Route path="accounts" element={<SocialAccountsSettings />} />
      </Route>
      {/* Composer. /compose (new draft) before /compose/:id (edit). */}
      <Route
        path="/social/compose"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.social}>
              <AppShell>
                <ComposePage />
              </AppShell>
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/social/compose/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.social}>
              <ComposeRoute />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Tasks — global open-tasks view */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <Tasks />
          </ProtectedRoute>
        }
      />

      {/* Notes — full quick-capture notebook */}
      <Route
        path="/notes"
        element={
          <ProtectedRoute>
            <NotesPage />
          </ProtectedRoute>
        }
      />

      {/* Jobs — background-job observability. Operator tooling, not an org
          feature: platform-admins only (nav entry carries the same gate). */}
      <Route
        path="/admin/platform/jobs"
        element={
          <PlatformAdminRoute>
            <Jobs />
          </PlatformAdminRoute>
        }
      />
      {/* Back-compat redirect for the former top-level Jobs route. */}
      <Route path="/jobs" element={<Navigate to="/admin/platform/jobs" replace />} />

      {/* Legacy integration routes — redirect to unified Settings with tab param */}
      <Route path="/integrations/email" element={<Navigate to="/settings?tab=email" replace />} />
      <Route path="/integrations/m365" element={<Navigate to="/settings?tab=integrations" replace />} />
      <Route path="/integrations/sms" element={<Navigate to="/settings?tab=sms" replace />} />
      <Route
        path="/settings/bookings"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.bookings}>
              <Navigate to="/settings?tab=bookings" replace />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/bookings/:pageId"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.bookings}>
              <BookingSettingsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      {/* Org-level settings — canonical home is now /admin/*. The former
          /settings/* paths redirect below (Task 9 / spec §5.2). Personal
          config (/settings?tab=profile, ?tab=notifications) is unaffected
          and stays on /settings. */}
      <Route
        path="/admin/automations"
        element={<ProtectedRoute><OrgAdminRoute><Automations /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/automations/dead-letter"
        element={<ProtectedRoute><OrgAdminRoute><AutomationsDeadLetter /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/automations/custom"
        element={<ProtectedRoute><OrgAdminRoute><CustomAutomations /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/automations/:workflowId"
        element={<ProtectedRoute><OrgAdminRoute><AutomationConfigure /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/reorder-rules"
        element={<ProtectedRoute><OrgAdminRoute><ReorderRules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/inactivity-rules"
        element={<ProtectedRoute><OrgAdminRoute><InactivityRules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/score-threshold-rules"
        element={<ProtectedRoute><OrgAdminRoute><ScoreThresholdRules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/date-rules"
        element={<ProtectedRoute><OrgAdminRoute><DateRules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/stage-rules"
        element={<ProtectedRoute><OrgAdminRoute><StageRules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/crm-fields"
        element={<ProtectedRoute><OrgAdminRoute><CrmFields /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/webhooks"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><WebhooksSettingsPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/webhooks/:id"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><WebhooksSettingsPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/portal"
        element={
          <ProtectedRoute>
            <OrgAdminRoute>
              <FeatureGateRoute gate={FEATURE_GATES.client_portal}>
                <SettingsLayout><PortalSettingsPage /></SettingsLayout>
              </FeatureGateRoute>
            </OrgAdminRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/branding"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><BrandingSettingsPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/modules"
        element={<ProtectedRoute><OrgAdminRoute><SettingsModules /></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/reviews"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><ReviewSettingsPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/referral-partners"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><ReferralPartnersPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/lead-routing"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><LeadRoutingPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/pipelines"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><PipelinesSettingsPage /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      <Route
        path="/admin/org-bookings"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><OrgBookingPages /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />
      {/* Same importer as /contacts/import, embedded in the settings shell
          so data import is discoverable from Settings → Workspace too. */}
      <Route
        path="/admin/import"
        element={<ProtectedRoute><OrgAdminRoute><SettingsLayout><ContactsImport chrome="none" /></SettingsLayout></OrgAdminRoute></ProtectedRoute>}
      />

      {/* Back-compat redirects — former org-settings paths now live under
          /admin. See ORG_ROUTE_REDIRECTS in lib/adminRoutes.ts. */}
      <Route path="/settings/automations" element={<Navigate to="/admin/automations" replace />} />
      <Route path="/settings/automations/dead-letter" element={<Navigate to="/admin/automations/dead-letter" replace />} />
      <Route path="/settings/automations/custom" element={<Navigate to="/admin/automations/custom" replace />} />
      <Route path="/settings/automations/:workflowId" element={<RedirectWithParams to="/admin/automations" />} />
      <Route path="/settings/reorder-rules" element={<Navigate to="/admin/reorder-rules" replace />} />
      <Route path="/settings/stage-rules" element={<Navigate to="/admin/stage-rules" replace />} />
      <Route path="/settings/crm-fields" element={<Navigate to="/admin/crm-fields" replace />} />
      <Route path="/settings/webhooks" element={<Navigate to="/admin/webhooks" replace />} />
      <Route path="/settings/webhooks/:id" element={<RedirectWithParams to="/admin/webhooks" />} />
      <Route path="/settings/portal" element={<Navigate to="/admin/portal" replace />} />
      <Route path="/settings/branding" element={<Navigate to="/admin/branding" replace />} />
      <Route path="/settings/modules" element={<Navigate to="/admin/modules" replace />} />
      <Route path="/settings/reviews" element={<Navigate to="/admin/reviews" replace />} />
      <Route path="/settings/referral-partners" element={<Navigate to="/admin/referral-partners" replace />} />
      <Route path="/settings/lead-routing" element={<Navigate to="/admin/lead-routing" replace />} />
      <Route path="/settings/pipelines" element={<Navigate to="/admin/pipelines" replace />} />
      <Route path="/settings/import" element={<Navigate to="/admin/import" replace />} />
      <Route
        path="/reviews"
        element={<ProtectedRoute><ReviewRequestsList /></ProtectedRoute>}
      />
      <Route
        path="/referrals"
        element={<ProtectedRoute><ReferralsList /></ProtectedRoute>}
      />

      {/* CI runs — operator/dev tooling for Relay itself, not customer
          configuration. Lives under the platform-admin console and is gated
          on the relay_platform_admin / super_admin role. */}
      <Route
        path="/admin/platform/ci"
        element={<PlatformAdminRoute><CiRuns /></PlatformAdminRoute>}
      />
      <Route
        path="/admin/platform/ci/:runId"
        element={<PlatformAdminRoute><CiRuns /></PlatformAdminRoute>}
      />
      {/* Back-compat redirects for the former customer-menu CI routes. */}
      <Route path="/settings/ci" element={<Navigate to="/admin/platform/ci" replace />} />
      <Route
        path="/settings/ci/:runId"
        element={<CiRunRedirect />}
      />

      {/* Bookings — host list view ("see my bookings inside Relay") */}
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.bookings}>
              <Bookings />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Reporting dashboard */}
      <Route
        path="/reporting"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReportingPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      {/* UGC board — deliverables by deadline + brands (renders its own shell). */}
      <Route
        path="/ugc"
        element={<ProtectedRoute><UgcBoardPage /></ProtectedRoute>}
      />

      {/* Reusable email templates (block editor) — used by sequences. */}
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <TemplatesPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Articles registry — blog posts available for campaign linkage. Gated
          by email_campaigns since the only real payoff is linking from an
          email campaign. */}
      <Route
        path="/articles"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <ArticlesPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Email Campaigns — list + editor. /emails/:id must come after /emails */}
      <Route
        path="/emails"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <EmailCampaignsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emails/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <EmailCampaignEditorPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/emails/:id/guided"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <EmailCampaignGuidedWizard />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* SMS Campaigns — list + editor + analytics. */}
      <Route
        path="/sms-campaigns"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sms_campaigns}>
              <SmsCampaignsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-campaigns/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sms_campaigns}>
              <SmsCampaignEditorPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-campaigns/:id/guided"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sms_campaigns}>
              <SmsCampaignGuidedWizard />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sms-campaigns/:id/analytics"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.sms_campaigns}>
              <SmsCampaignAnalyticsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Projects — admin list + detail (post-deal delivery workspace) */}
      <Route
        path="/projects"
        element={<ProtectedRoute><ProjectsListPage /></ProtectedRoute>}
      />
      <Route
        path="/projects/:id"
        element={<ProtectedRoute><ProjectDetailPage /></ProtectedRoute>}
      />

      {/* Documents — admin list + editor */}
      <Route
        path="/documents"
        element={<ProtectedRoute><DocumentsListPage /></ProtectedRoute>}
      />
      <Route
        path="/documents/:id"
        element={<ProtectedRoute><DocumentEditorPage /></ProtectedRoute>}
      />

      {/* Finance dashboard — owner/admin + Forge finance-read role (the same
          predicate the AR/AP HUD cards declare). FeatureGateRoute is plan-based,
          so the role gate gets its own wrapper (FinanceRoute, mirroring
          AgencyRoute). */}
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <FinanceRoute>
              <FinanceDashboardPage />
            </FinanceRoute>
          </ProtectedRoute>
        }
      />
      {/* Books: opt-in ledger addon, existing admin setup and server-authorized read. */}
      {(
        [
          ["/books", <BooksOverviewPage key="o" />],
          ["/books/setup", <BooksSetupPage key="s" />],
          ["/books/accounts", <LedgerAccountsPage key="a" />],
          ["/books/journal", <JournalPage key="j" />],
          ["/books/journal/new", <EntryEditorPage key="n" />],
          ["/books/journal/:entryId", <EntryDetailPage key="d" />],
          ["/books/journal/:entryId/edit", <EntryEditorPage key="e" />],
          ["/books/ledger", <GeneralLedgerPage key="l" />],
          ["/books/trial-balance", <TrialBalancePage key="t" />],
          ["/books/close", <ClosePage key="c" />],
        ] as const
      ).map(([path, page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <FeatureAddonRoute addon="ledger">
                <BooksRoute>{page}</BooksRoute>
              </FeatureAddonRoute>
            </ProtectedRoute>
          }
        />
      ))}

      {/* Agency self-serve — gated on org_type === 'agency' + owner/admin role.
          /agency/clients/:id must come before /agency/clients (specificity). */}
      <Route
        path="/agency"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.agency}>
              <AgencyRoute>
                <AgencyDashboard />
              </AgencyRoute>
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agency/clients"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.agency}>
              <AgencyRoute>
                <AgencyClientList />
              </AgencyRoute>
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agency/clients/:id"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.agency}>
              <AgencyRoute>
                <AgencyClientDetail />
              </AgencyRoute>
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agency/plans"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.agency}>
              <AgencyRoute>
                <AgencyPlanEditor />
              </AgencyRoute>
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* The Admin Portal dashboard was retired (#35); /admin lands on Settings. */}
      <Route path="/admin" element={<Navigate to="/settings" replace />} />

      {/* Platform admin — cross-tenant operator console for Relay.
          Gated on relay_platform_admin / super_admin JWT role. */}
      <Route
        path="/admin/platform/organizations"
        element={
          <PlatformAdminRoute>
            <PlatformOrganizations />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/organizations/:orgId"
        element={
          <PlatformAdminRoute>
            <PlatformOrganizationDetail />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/users"
        element={
          <PlatformAdminRoute>
            <PlatformUsers />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/users/:userId"
        element={
          <PlatformAdminRoute>
            <PlatformUserDetail />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/audit"
        element={
          <PlatformAdminRoute>
            <PlatformAuditLogs />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/observability"
        element={
          <PlatformAdminRoute>
            <PlatformObservability />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/billing"
        element={
          <PlatformAdminRoute>
            <PlatformBilling />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/support-sessions"
        element={
          <PlatformAdminRoute>
            <PlatformSupportSessions />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/impersonation"
        element={
          <PlatformAdminRoute>
            <PlatformImpersonation />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform/feature-flags"
        element={
          <PlatformAdminRoute>
            <PlatformFeatureFlags />
          </PlatformAdminRoute>
        }
      />
      <Route
        path="/admin/platform"
        element={
          <PlatformAdminRoute>
            <PlatformDashboard />
          </PlatformAdminRoute>
        }
      />

      {/* Settings — unified admin page with tabs: General, Profile, Team,
          Pipeline, Email, Bookings, Integrations, SMS */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />

      {/* Public review landing — no auth required */}
      <Route
        path="/review/:token"
        element={<Suspense fallback={null}><PublicReviewPage /></Suspense>}
      />

      {/* Public unsubscribe — redirects to Edge Function; no auth needed */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />

      {/* Email Campaign analytics — /emails/:id/analytics must come after /emails/:id */}
      <Route
        path="/emails/:id/analytics"
        element={
          <ProtectedRoute>
            <FeatureGateRoute gate={FEATURE_GATES.email_campaigns}>
              <EmailCampaignAnalyticsPage />
            </FeatureGateRoute>
          </ProtectedRoute>
        }
      />

      {/* Calls — history table (functional) + gated click-to-call UI */}
      <Route
        path="/calls"
        element={
          <ProtectedRoute>
            <CallsPage />
          </ProtectedRoute>
        }
      />

      {/* Root + unknown paths route to /dashboard, which itself bounces to
          /login if the user isn't signed in. */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}
